import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  lang?: string;
  noindex?: boolean;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  lang,
  noindex = false,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const setProperty = (property: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (noindex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow');
    }

    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) existingCanonical.remove();
    if (canonical) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonical);
      document.head.appendChild(link);
    }

    setProperty('og:title', title);
    setProperty('og:description', description);
    setProperty('og:type', ogType);
    if (ogImage) {
      setProperty('og:image', ogImage);
    } else {
      const existingOgImage = document.querySelector('meta[property="og:image"]');
      if (existingOgImage) existingOgImage.remove();
    }

    const setTwitterMeta = (name: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setTwitterMeta('twitter:title', title);
    setTwitterMeta('twitter:description', description);
    if (ogImage) {
      setTwitterMeta('twitter:image', ogImage);
    } else {
      const existingTwImage = document.querySelector('meta[name="twitter:image"]');
      if (existingTwImage) existingTwImage.remove();
    }

    if (lang) {
      document.documentElement.lang = lang;
    }

    return () => {
      // No cleanup needed for document head
    };
  }, [title, description, keywords, canonical, ogImage, ogType, lang, noindex]);

  return null;
}